import axios from 'axios'
import { useLocation } from 'react-router-dom'
import { useEffect } from 'react'

const ensureTrailingSlash = (url) =>
  url.endsWith('/') ? url : `${url}/`

const AUTH_SESSION_KEYS = [
  'token',
  'refresh_token',
  'token_type',
  'user_id',
  'is_admin',
  'timezone',
]

let refreshTokenPromise = null

// Function to determine the domain based on the current URL
// https://tranquilla-ai-dev.mresult.com/
export const getDomain = () => {
  const configuredBaseUrl = process.env.REACT_APP_API_BASE_URL

  if (configuredBaseUrl) {
    return ensureTrailingSlash(configuredBaseUrl)
  }

  const url = window.location.href
  if (url.includes('localhost')) {
    return 'https://tranquilla-ai.nearlyhuman.ai/'

  } else if (url.includes('tranquilla.nearlyhuman.ai')) {
    return 'https://tranquilla-ai.nearlyhuman.ai/'
  } else if (url.includes('https://tranquilla.mresult.com/')) {
    return 'https://tranquilla-ai.mresult.com/'
  } else if (url.includes('https://tranquilla-dev.mresult.com/')) {
    return 'https://tranquilla-ai-dev.mresult.com/'
  } else if (url.includes('https://tranquilla-dev.nearlyhuman.ai/')) {
    return 'https://tranquilla-ai-dev.nearlyhuman.ai/'
  }
  return 'https://tranquilla-ai-dev.mresult.com/' // Default domain
}

// Get the dynamic domain
const domain = getDomain()

const setSessionValue = (key, value) => {
  if (value === undefined || value === null || value === '') {
    sessionStorage.removeItem(key)
    return
  }

  sessionStorage.setItem(key, String(value))
}

export const persistAuthSession = (authData = {}) => {
  const {
    access_token,
    refresh_token,
    token_type,
    user_id,
    is_admin,
    timezone,
  } = authData

  setSessionValue('token', access_token)
  setSessionValue('refresh_token', refresh_token)
  setSessionValue('user_id', user_id)
  setSessionValue('token_type', token_type)

  if (is_admin !== undefined) {
    setSessionValue('is_admin', is_admin)
  }

  setSessionValue('timezone', timezone)
}

export const clearAuthSession = () => {
  AUTH_SESSION_KEYS.forEach((key) => sessionStorage.removeItem(key))
}

const getDeviceInfo = () => {
  if (typeof navigator !== 'undefined' && navigator.userAgent) {
    return navigator.userAgent
  }

  return 'web'
}

const withAuthHeaders = (headers = {}) => {
  const nextHeaders = new Headers(headers)
  const token = sessionStorage.getItem('token')

  if (token) {
    nextHeaders.set('Authorization', `Bearer ${token}`)
  } else {
    nextHeaders.delete('Authorization')
  }

  return nextHeaders
}

const shouldRefreshAuth = (response) =>
  response && (response.status === 401 || response.status === 403)

export const refreshAccessToken = async () => {
  if (refreshTokenPromise) {
    return refreshTokenPromise
  }

  const refreshToken = sessionStorage.getItem('refresh_token')

  if (!refreshToken) {
    throw new Error('No refresh token available')
  }

  refreshTokenPromise = (async () => {
    const response = await fetch(`${getDomain()}refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        refresh_token: refreshToken,
        device_info: getDeviceInfo(),
      }),
    })

    if (!response.ok) {
      clearAuthSession()
      throw new Error('Unable to refresh session')
    }

    const authData = await response.json()
    persistAuthSession(authData)
    return authData.access_token
  })()

  try {
    return await refreshTokenPromise
  } finally {
    refreshTokenPromise = null
  }
}

export const authFetch = async (input, init = {}) => {
  const { skipAuthRefresh = false, ...fetchInit } = init
  const requestInit = {
    ...fetchInit,
    headers: withAuthHeaders(fetchInit.headers),
  }

  const response = await fetch(input, requestInit)

  if (
    skipAuthRefresh ||
    !shouldRefreshAuth(response) ||
    !sessionStorage.getItem('refresh_token')
  ) {
    return response
  }

  try {
    await refreshAccessToken()
  } catch {
    return response
  }

  return fetch(input, {
    ...fetchInit,
    headers: withAuthHeaders(fetchInit.headers),
  })
}

export const fetchEULAandPrivacyPolicy = async () => {
  const res = await fetch(`${getDomain()}legal-document/latest`)
  if (!res.ok) throw new Error('Failed to fetch policies')
  const data = await res.json();
  return data;
}

export const acceptTOS = async (payload) => {
  try {
    const res = await fetch(`${getDomain()}accept-tos`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
    return res;
  } catch (err) {
    console.error('Error accepting TOS:', err)
  }


}

export const checkIfPoliciesAccepted = async (user) => {
  try {
    await fetch(`${getDomain()}user/${user}/latest-tos-acceptance`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
      },
    })
  } catch (err) {
    console.error(err);
  }
}

// Function to create axios instance with dynamic baseURL
const createApiInstance = (baseURL) => {
  return axios.create({
    baseURL: baseURL,
  })
}

// Axios API calls
export const getData = async (endpoint) => {
  const api = createApiInstance(sessionStorage.getItem('baseURL') || domain)
  try {
    const response = await api.get(endpoint)
    return response.data
  } catch (error) {
    console.error('Error fetching data:', error)
    throw error
  }
}


export const postData = async (endpoint, data, headers = {}) => {
  const api = createApiInstance(sessionStorage.getItem('baseURL') || domain)
  try {
    const response = await api.post(endpoint, data, { headers })
    if (endpoint === '/sign-up') {
      return response
    }
    return response.data
  } catch (error) {
    console.error('Error posting data:', error)
    throw error
  }
}

export const putData = async (endpoint, data) => {
  const api = createApiInstance(sessionStorage.getItem('baseURL') || domain)
  try {
    const response = await api.put(endpoint, data)
    return response.data
  } catch (error) {
    console.error('Error posting data:', error)
    throw error
  }
}

export const deleteData = async (endpoint, data) => {
  const api = createApiInstance(sessionStorage.getItem('baseURL') || domain)
  try {
    const response = await api.delete(endpoint, data)
    return response.data
  } catch (error) {
    console.error('Error posting data:', error)
    throw error
  }
}

// Hook to manage query parameter changes for dynamic baseURL
export const useQueryParamHandler = () => {
  const location = useLocation()

  useEffect(() => {
    const queryString = location.search
    const queryParams = new URLSearchParams(queryString)

    const agentNameParam = queryParams.get('agentName')

    if (agentNameParam !== null) {
      const BASE_URL = `${domain}${agentNameParam}/`
      sessionStorage.setItem('baseURL', BASE_URL)
      sessionStorage.setItem('userType', 'user')
      sessionStorage.setItem('agentName', agentNameParam)
    } else {
      sessionStorage.setItem('baseURL', domain)
      sessionStorage.setItem('userType', 'admin')
      sessionStorage.removeItem('agentName')
    }
  }, [location]) // Dependency on location so it re-runs whenever the URL changes
}
